import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { Building2 } from 'lucide-react';
import { useSupervisorDashboardData } from './useSupervisorDashboardData';
import { Select } from '@/components/ui/Select';
import { BusinessHealthRow } from '@/features/dashboard/BusinessHealthRow';
import { ActionCenterCard } from '@/features/dashboard/ActionCenterCard';
import { OccupancyOverviewCard } from '@/features/dashboard/OccupancyOverviewCard';
import { RevenueSnapshotCard } from '@/features/dashboard/RevenueSnapshotCard';
import { PropertyOverviewTable } from '@/features/dashboard/PropertyOverviewTable';
import { buildRentSearch, buildTenantSearch, pickHostelIdByBedCount } from '@/features/dashboard/drillDownTargets';
import type { DrillDownKind } from '@/features/dashboard/types';

export function SupervisorDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const {
    hostels, isLoading,
    activeHostels, multiHostel, selectedId, setSelectedId, isAll, activeHostelId,
    s, perHostelStats, revenueData,
  } = useSupervisorDashboardData();
  const navigate = useNavigate();

  function handleDrillDown(kind: DrillDownKind) {
    switch (kind) {
      case 'OVERDUE_RENT':
      case 'PENDING_RENT':
      case 'EXPECTED_TODAY':
        void navigate({ to: '/supervisor/rent', search: buildRentSearch(kind, activeHostelId) });
        break;
      case 'CHECKIN_TODAY':
      case 'CHECKOUT_TOMORROW':
      case 'STAY_ENDING_SOON':
      case 'OCCUPIED':
      case 'RESERVED':
        void navigate({ to: '/supervisor/tenants', search: buildTenantSearch(kind, activeHostelId) });
        break;
      case 'MAINTENANCE':
      case 'AVAILABLE': {
        const countSelector = kind === 'MAINTENANCE'
          ? (stats: typeof s) => stats.blockedBeds
          : (stats: typeof s) => stats.vacantBeds;
        const targetHostelId = pickHostelIdByBedCount(activeHostelId, perHostelStats, countSelector);
        if (targetHostelId) {
          void navigate({
            to: '/supervisor/hostels/$id',
            params: { id: targetHostelId },
            search: { bedStatus: kind === 'MAINTENANCE' ? 'MAINTENANCE' : 'VACANT' },
          });
        }
        break;
      }
    }
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.welcome', { name: user?.fullName })}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('dashboard.supervisorSubtitle')}</p>
        </div>
        {multiHostel && (
          <Select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            options={[
              { value: 'ALL', label: t('dashboard.allHostels') },
              ...activeHostels.map(h => ({ value: h.id, label: h.name })),
            ]}
          />
        )}
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl p-10 text-center border border-gray-100 mb-6">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('common.loading')}</p>
        </div>
      )}

      {!isLoading && (activeHostelId || isAll) && (
        <>
          {/* Section 1 — Business Health */}
          <BusinessHealthRow stats={s} onSelect={handleDrillDown} hoverBorderClassName="hover:border-violet-300" />

          {/* Section 2 — Action Center */}
          <ActionCenterCard stats={s} onSelect={handleDrillDown} />

          {/* Section 3 & 4 — Occupancy Overview + Revenue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <OccupancyOverviewCard stats={s} onSelect={handleDrillDown} />
            <RevenueSnapshotCard stats={s} revenueData={revenueData} onSelectPendingRent={handleDrillDown} accentHex="#7c3aed" />
          </div>

          {/* Section 5 — Property Overview */}
          <PropertyOverviewTable entries={perHostelStats} role="supervisor" />
        </>
      )}

      {!isLoading && hostels.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <Building2 size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t('dashboard.noHostelsAssigned')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('dashboard.contactOwner')}</p>
        </div>
      ) : null}
    </div>
  );
}
